import React, { useEffect, useState } from 'react';
import { MapContainer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import OsmTiles from '../../components/OsmTiles';
import { collection, getDocs, query, orderBy, limit, doc, updateDoc, deleteDoc, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { CheckIn } from '../../types';
import { exportToCSV } from '../../utils/export';
import { haversine } from '../../utils/route';
import { fmtBRL, fmtDuration } from '../../utils/reportStats';
import { useAuth } from '../../contexts/AuthContext';
import { useAccessScope } from '../../hooks/useAccessScope';
import { fetchCheckinsEscopado } from '../../services/checkinsQuery';
import Pagination, { usePagination } from '../../components/Pagination';
import FotoThumb from '../../components/FotoThumb';
import 'leaflet/dist/leaflet.css';

const RESULTADO_LABEL: Record<string, string> = {
  comprador_atendeu: 'Comprador Atendeu', comprador_ausente: 'Comprador Ausente', comprador_nao_atendeu: 'Comprador Não Atendeu',
  realizada: 'Realizada', contato_frio: 'Contato frio', sem_interesse: 'Sem interesse',
};
const pedidoLabel = (ci: CheckIn): string => {
  const st = ci.pedidoStatus || (ci.pedidoFeito ? 'sim' : 'nao');
  if (st === 'sim') return `Pedido${ci.pedidoValor ? ` — ${fmtBRL(ci.pedidoValor)}` : ''}`;
  if (st === 'orcamento') return `Orçamento${ci.pedidoValor ? ` — ${fmtBRL(ci.pedidoValor)}` : ''}`;
  return 'Sem pedido';
};

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => { if (points.length) { try { map.fitBounds(points as any, { padding: [30, 30], maxZoom: 16 }); } catch { /* ignore */ } } }, [points, map]);
  return null;
}

const somaKm = (pts: [number, number][]) => {
  let m = 0;
  for (let i = 1; i < pts.length; i++) m += haversine({ lat: pts[i - 1][0], lng: pts[i - 1][1] }, { lat: pts[i][0], lng: pts[i][1] });
  return m / 1000;
};
const fmtHora = (d: Date) => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

// Converte UM check-in cru do Firestore. Existe como função porque o mapeamento é usado por DUAS
// consultas (a lista normal e a das possíveis duplicadas) e a cópia esquecia o `trajeto.startedAt` —
// um Timestamp cru chegava ao modal de trajeto, que chama toLocaleTimeString nele, e o painel inteiro
// ia para tela branca (não há ErrorBoundary). Mapeamento duplicado parcial é a causa raiz; por isso
// toda consulta nova desta tela deve passar por aqui.
// Teto da consulta de suspeitas. A lista é curta por natureza; o teto existe para a tela não virar um
// download de milhares de documentos se algum dia algo marcar em massa. Ao bater no teto, o aviso diz
// que está mostrando as mais recentes em vez de afirmar um número falso.
const TETO_DUPLICADAS = 300;

const hidratar = (raw: any): CheckIn => ({
  ...raw,
  createdAt: raw.createdAt?.toDate?.() ?? new Date(),
  checkoutAt: raw.checkoutAt?.toDate?.() ?? null,
  trajeto: raw.trajeto ? { ...raw.trajeto, startedAt: raw.trajeto.startedAt?.toDate?.() ?? null } : raw.trajeto,
});

export default function HistoryPage() {
  const { user, isAdmin, isStaff } = useAuth();
  const scope = useAccessScope();
  const [checkins, setCheckins] = useState<CheckIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSeller, setFilterSeller] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CheckIn | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [trajeto, setTrajeto] = useState<CheckIn | null>(null);
  const [detail, setDetail] = useState<CheckIn | null>(null);
  const [matchedTrails, setMatchedTrails] = useState<Record<string, [number, number][]>>({}); // trajeto encaixado por check-in (cache)
  // POSSÍVEIS DUPLICADAS — consulta PRÓPRIA, não um filtro sobre a lista de baixo. A lista normal traz
  // os 500 check-ins mais recentes, que com 236 vendedores cobre uma fração de um dia: as duas visitas
  // de um par suspeito quase nunca cairiam juntas nessa janela. Quem marca é o servidor
  // (functions: marcarVisitasDuplicadas) e a marca some sozinha quando uma das duas recebe check-out.
  const [duplicadas, setDuplicadas] = useState<CheckIn[]>([]);
  const [soDuplicadas, setSoDuplicadas] = useState(false);

  const load = async () => {
    // Busca ESCOPADA no servidor: acesso total lê tudo; gestor só a equipe (lotes de sellerId);
    // vendedor só as próprias. A regra recusa consulta ampla de gestor, daí o helper.
    const raws = await fetchCheckinsEscopado(user, scope, { limitTotal: 500, ordem: 'desc', ordenarNoServidor: true });
    const data = raws.map(hidratar);
    setCheckins(data);
    setLoading(false);
    // Best-effort e FORA do caminho crítico: se falhar, a página funciona como sempre funcionou —
    // o aviso simplesmente não aparece. Nunca derruba o Histórico por causa de um aviso.
    try {
      // `limitTotal` explícito: sem ele o teto do ramo do GESTOR valeria por LOTE de 30 vendedores,
      // não no total. Ordenação no cliente de propósito — `orderBy` junto da igualdade exigiria índice
      // composto novo, e a lista de suspeitas é curta.
      const dups = await fetchCheckinsEscopado(user, scope, { somenteDuplicadas: true, limitTotal: TETO_DUPLICADAS });
      setDuplicadas((dups.map(hidratar) as CheckIn[]).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
    } catch (e) {
      console.warn('Não foi possível carregar as possíveis duplicadas:', e);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ao abrir "Ver trajeto", encaixa o trajeto real nas ruas (map-matching via ORS).
  // Cache por check-in: só chama o ORS na 1ª vez que aquele trajeto é aberto.
  useEffect(() => {
    const id = trajeto?.id;
    const trail = trajeto?.trajeto?.trail;
    if (!id || !trail || trail.length < 2 || matchedTrails[id]) return;
    let cancel = false;
    httpsCallable(functions, 'mapMatchTrail')({ coordinates: trail.map(p => ({ lat: p.lat, lng: p.lng })) })
      .then((r: any) => { if (!cancel && Array.isArray(r?.data?.trail)) setMatchedTrails(prev => ({ ...prev, [id]: r.data.trail.map((p: any) => [p.lat, p.lng] as [number, number]) })); })
      .catch(() => { /* mantém o trajeto original */ });
    return () => { cancel = true; };
  }, [trajeto, matchedTrails]);

  // Gestor: restringe aos check-ins dos vendedores da hierarquia dele.
  const scoped = scope.full ? checkins : checkins.filter(c => (scope.sellerIds || []).includes(c.sellerId));
  const sellers = Array.from(new Set(scoped.map(c => c.sellerName))).sort();

  // Ids suspeitos, para o selo na linha. Vem da consulta própria, então uma linha da lista normal
  // recebe o selo mesmo quando a irmã dela está fora dos 500 mais recentes.
  const idsDuplicadas = new Set(duplicadas.map(c => c.id));
  const baseLista = soDuplicadas
    ? (scope.full ? duplicadas : duplicadas.filter(c => (scope.sellerIds || []).includes(c.sellerId)))
    : scoped;

  const filtered = baseLista.filter(c => {
    const matchesSeller = filterSeller === 'all' || c.sellerName === filterSeller;
    const matchesSearch = search === '' ||
      c.clientName.toLowerCase().includes(search.toLowerCase()) ||
      c.sellerName.toLowerCase().includes(search.toLowerCase());
    return matchesSeller && matchesSearch;
  });

  // Paginação (componente compartilhado — 25 linhas por página por padrão)
  const pager = usePagination(filtered, `${search}|${filterSeller}|${soDuplicadas}`);
  const pageItems = pager.pageItems;

  const formatDate = (d: Date) => d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const openEdit = (ci: CheckIn) => {
    setEditing(ci);
    setEditNotes(ci.notes || '');
  };

  const saveEdit = async () => {
    if (!editing) return;
    await updateDoc(doc(db, 'checkins', editing.id), { notes: editNotes.trim() || null });
    setCheckins(prev => prev.map(c => c.id === editing.id ? { ...c, notes: editNotes.trim() || undefined } : c));
    setDuplicadas(prev => prev.map(c => c.id === editing.id ? { ...c, notes: editNotes.trim() || undefined } : c));
    setEditing(null);
  };

  const handleDelete = async (ci: CheckIn) => {
    const ok = window.confirm(`Excluir o check-in de "${ci.clientName}" feito por ${ci.sellerName} em ${formatDate(ci.createdAt)}?\n\nEsta ação não pode ser desfeita.`);
    if (!ok) return;
    await deleteDoc(doc(db, 'checkins', ci.id));
    setCheckins(prev => prev.filter(c => c.id !== ci.id));
    // ⚠️ NÃO recarregar aqui. A marca da IRMÃ é removida pelo servidor (functions:
    // limparMarcaDuplicada), mas por Eventarc — segundos depois, mais ainda com cold start. Um
    // `load()` logo após o delete PERDE essa corrida e repinta o selo "possível duplicada" numa
    // visita que acabou de deixar de ter par. Isso não é cosmético: o gestor que está limpando
    // duplicatas leria o selo remanescente e excluiria a outra também — apagando a ÚNICA visita real
    // daquele cliente, de forma irreversível. Então o painel antecipa localmente o mesmo resultado
    // que o servidor vai gravar; a próxima abertura da tela confirma pelo servidor.
    setDuplicadas(prev => {
      const semEla = prev.filter(c => c.id !== ci.id);
      if (!ci.possivelDuplicada) return semEla;
      const aindaTemPar = (c: CheckIn) => semEla.some(o => o.id !== c.id && o.sellerId === c.sellerId && o.clientId === c.clientId);
      return semEla.filter(aindaTemPar);
    });
  };

  const handleExport = () => {
    const rows = filtered.map(ci => ({
      CheckIn: formatDate(ci.createdAt),
      CheckOut: ci.checkoutAt ? formatDate(ci.checkoutAt) : '',
      Vendedor: ci.sellerName,
      Cliente: ci.clientName,
      Motivos: (ci.reasons || []).join(', '),
      Observacao: ci.notes || '',
      Latitude: ci.location.latitude,
      Longitude: ci.location.longitude,
    }));
    exportToCSV(rows, `historico-visitas-${new Date().toISOString().slice(0, 10)}`);
  };

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>{isAdmin ? 'Histórico de Visitas' : 'Minhas Visitas'}</h1>
          <p style={styles.subtitle}>{filtered.length} registros</p>
        </div>
        <button style={styles.exportBtn} onClick={handleExport}>⬇️ Exportar Excel</button>
      </div>

      <div style={styles.filters}>
        {/* Avisa e some quando o filtro já está ligado — repetir a contagem na tela que já mostra
            só elas seria ruído. Nenhum botão de ação aqui de propósito: o servidor só LEVANTA A MÃO,
            quem encerra a visita é o vendedor pelo caminho normal de check-out. */}
        {duplicadas.length > 0 && !soDuplicadas && (
          <div style={styles.avisoDup}>
            <span style={styles.avisoDupTexto}>
              ⚠️ {duplicadas.length === TETO_DUPLICADAS ? `as ${TETO_DUPLICADAS} mais recentes` : `${duplicadas.length}`} visita(s) podem estar duplicadas — o mesmo vendedor tem outra visita em aberto no mesmo cliente.
            </span>
            <button style={styles.avisoDupLink} onClick={() => setSoDuplicadas(true)}>ver só essas</button>
          </div>
        )}
        {(duplicadas.length > 0 || soDuplicadas) && (
          <button style={styles.select} onClick={() => setSoDuplicadas(v => !v)}>
            {soDuplicadas ? 'Mostrar todas' : 'Mostrar só as suspeitas'}
          </button>
        )}
        <input
          style={styles.search}
          placeholder="Buscar por cliente ou vendedor..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isAdmin && (
          <select style={styles.select} value={filterSeller} onChange={e => setFilterSeller(e.target.value)}>
            <option value="all">Todos os vendedores</option>
            {sellers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {(isAdmin
                  ? ['Check-in', 'Check-out', 'Vendedor', 'Cliente', 'Motivos', 'Observação', 'GPS', 'Fotos', 'Ações']
                  : ['Check-in', 'Check-out', 'Cliente', 'Motivos', 'Observação', 'GPS', 'Fotos']
                ).map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageItems.map(ci => (
                <tr key={ci.id} style={{ ...styles.tr, cursor: 'pointer', ...(idsDuplicadas.has(ci.id) ? { background: '#fffbeb' } : {}) }} onClick={() => setDetail(ci)} title="Ver detalhes da visita">
                  <td style={styles.td}>{formatDate(ci.createdAt)}</td>
                  <td style={styles.td}>
                    {ci.checkoutAt
                      ? <span style={{ color: '#dc2626', fontWeight: 600 }}>{formatDate(ci.checkoutAt)}</span>
                      : <span style={{ color: '#d97706', fontSize: 12, fontWeight: 600 }}>Em aberto</span>}
                    {idsDuplicadas.has(ci.id) && (
                      <div style={{ marginTop: 3 }}>
                        <span title="O mesmo vendedor tem outra visita em aberto neste cliente, a menos de 12 h. Nada foi alterado — confira e encerre a correta." style={styles.seloDup}>possível duplicada</span>
                      </div>
                    )}
                  </td>
                  {isAdmin && <td style={styles.td}><strong>{ci.sellerName}</strong></td>}
                  <td style={styles.td}>
                    <div>{ci.clientName}</div>
                    {ci.foraDoRaio && (
                      <span title={`Check-in fora do raio${ci.distanciaMetros != null ? ` (${ci.distanciaMetros}m)` : ''} — coordenada do cliente pode estar errada`} style={{ display: 'inline-block', marginTop: 2, fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>
                        ⚠️ fora do raio{ci.distanciaMetros != null ? ` ${(ci.distanciaMetros / 1000).toFixed(1)}km` : ''}
                      </span>
                    )}
                  </td>
                  <td style={{ ...styles.td, maxWidth: 180, fontSize: 12 }}>
                    {(ci.reasons || []).join(', ') || '—'}
                  </td>
                  <td style={{ ...styles.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ci.notes || '—'}
                  </td>
                  <td style={styles.td}>
                    <a
                      href={`https://www.google.com/maps?q=${ci.location.latitude},${ci.location.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{ color: '#f5821f', fontSize: 12 }}
                    >
                      {ci.location.latitude.toFixed(4)}, {ci.location.longitude.toFixed(4)}
                    </a>
                    {(ci.trajeto?.trail?.length || ci.trajeto?.suggestedRoute?.length) ? (
                      <button style={styles.trajetoBtn} onClick={e => { e.stopPropagation(); setTrajeto(ci); }}>🗺️ Trajeto</button>
                    ) : null}
                  </td>
                  <td style={styles.td}>
                    {((ci.photos?.length || 0) + (ci.checkoutPhotos?.length || 0)) > 0 ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(ci.photos || []).map((url, i) => (
                          <a key={'in' + i} href={url} target="_blank" rel="noreferrer" title="Foto do check-in" onClick={e => e.stopPropagation()}>
                            <FotoThumb uri={url} style={styles.photoThumb} />
                          </a>
                        ))}
                        {(ci.checkoutPhotos || []).map((url, i) => (
                          <a key={'out' + i} href={url} target="_blank" rel="noreferrer" title="Foto do check-out" onClick={e => e.stopPropagation()}>
                            <FotoThumb uri={url} style={{ ...styles.photoThumb, borderColor: '#dc2626' }} />
                          </a>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  {isAdmin && (
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                      <button style={styles.actionBtn} onClick={e => { e.stopPropagation(); openEdit(ci); }}>Editar</button>
                      <button style={styles.deleteBtn} onClick={e => { e.stopPropagation(); handleDelete(ci); }}>Excluir</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p style={{ textAlign: 'center', color: '#999', padding: 32 }}>Nenhum check-in encontrado.</p>}
        </div>
      )}

      {!loading && <Pagination pager={pager} label="registros" />}

      {trajeto && trajeto.trajeto && (() => {
        const t = trajeto.trajeto!;
        const sugg = (t.suggestedRoute || []).map(p => [p.lat, p.lng] as [number, number]);
        // Usa o trajeto encaixado nas ruas quando disponível; senão, os pontos brutos de GPS.
        const matched = matchedTrails[trajeto.id];
        const real: [number, number][] = matched ? [...matched] : (t.trail || []).map(p => [p.lat, p.lng] as [number, number]);
        const arrival: [number, number] = [trajeto.location.latitude, trajeto.location.longitude];
        if (real.length) real.push(arrival);
        const hasStart = t.startLat != null && t.startLng != null;
        const all: [number, number][] = [...sugg, ...real, arrival, ...(hasStart ? [[t.startLat as number, t.startLng as number] as [number, number]] : [])];
        return (
          <div style={styles.overlay} onClick={() => setTrajeto(null)}>
            <div style={{ ...styles.modal, maxWidth: 700, padding: 0, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
              <div style={styles.trajetoHeader}>
                <div>
                  <h2 style={styles.modalTitle}>Trajeto até o cliente</h2>
                  <p style={styles.modalSub}>{trajeto.clientName} • {trajeto.sellerName} • {formatDate(trajeto.createdAt)}</p>
                </div>
                <button style={styles.cancelBtn} onClick={() => setTrajeto(null)}>Fechar</button>
              </div>
              <div style={{ height: 420 }}>
                <MapContainer center={arrival} zoom={13} style={{ height: '100%', width: '100%' }}>
                  <OsmTiles />
                  <FitBounds points={all} />
                  {sugg.length > 1 && <Polyline positions={sugg} pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.6, dashArray: '8 8' }} />}
                  {real.length > 1 && <Polyline positions={real} pathOptions={{ color: '#f5821f', weight: 4, opacity: 0.9 }} />}
                  {hasStart && (
                    <CircleMarker center={[t.startLat as number, t.startLng as number]} radius={8} pathOptions={{ color: '#fff', weight: 2, fillColor: '#16a34a', fillOpacity: 1 }}>
                      <Tooltip permanent direction="top">Partida</Tooltip>
                    </CircleMarker>
                  )}
                  <CircleMarker center={arrival} radius={8} pathOptions={{ color: '#fff', weight: 2, fillColor: '#dc2626', fillOpacity: 1 }}>
                    <Tooltip permanent direction="top">Chegada</Tooltip>
                  </CircleMarker>
                </MapContainer>
              </div>
              <div style={styles.trajetoLegend}>
                <span><span style={{ ...styles.swatch, background: '#f5821f' }} /> Trajeto real: {somaKm(real).toFixed(1)} km</span>
                <span><span style={{ ...styles.swatch, background: '#2563eb' }} /> Rota sugerida: {somaKm(sugg).toFixed(1)} km</span>
              </div>
              <div style={styles.trajetoLegend}>
                {t.startedAt && <span>🚗 Início da navegação: <strong>{fmtHora(t.startedAt)}</strong></span>}
                {t.startedAt && <span>⏱️ Tempo até chegar: <strong>{fmtDuration(trajeto.createdAt.getTime() - t.startedAt.getTime())}</strong></span>}
                <span>🏁 Chegada no cliente: <strong>{fmtHora(trajeto.createdAt)}</strong></span>
              </div>
              {!t.startedAt && <p style={{ ...styles.modalSub, padding: '0 20px 12px' }}>Horário de início não disponível para esta visita (rastreamento anterior à atualização).</p>}
            </div>
          </div>
        );
      })()}

      {detail && (() => {
        const ci = detail;
        const permanencia = ci.checkoutAt ? ci.checkoutAt.getTime() - ci.createdAt.getTime() : null;
        const temTrajeto = !!(ci.trajeto?.trail?.length || ci.trajeto?.suggestedRoute?.length);
        return (
          <div style={styles.overlay} onClick={() => setDetail(null)}>
            <div style={{ ...styles.modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>Detalhes da visita</h2>
              <p style={styles.modalSub}>{ci.clientName}{isAdmin ? ` • ${ci.sellerName}` : ''}</p>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}><span style={styles.detailK}>Check-in</span><span>{formatDate(ci.createdAt)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailK}>Check-out</span><span>{ci.checkoutAt ? formatDate(ci.checkoutAt) : <em style={{ color: '#d97706' }}>Em aberto</em>}</span></div>
                <div style={styles.detailItem}><span style={styles.detailK}>Permanência</span><span>{fmtDuration(permanencia)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailK}>Resultado</span><span>{ci.resultado ? (RESULTADO_LABEL[ci.resultado] || ci.resultado) : '—'}</span></div>
                <div style={styles.detailItem}><span style={styles.detailK}>Pedido</span><span>{pedidoLabel(ci)}</span></div>
                <div style={styles.detailItem}><span style={styles.detailK}>Motivos</span><span>{(ci.reasons || []).join(', ') || '—'}</span></div>
              </div>
              {ci.foraDoRaio && (
                <div style={styles.detailWarn}>⚠️ Check-in feito fora do raio{ci.distanciaMetros != null ? ` — ${ci.distanciaMetros}m do ponto cadastrado` : ''} (a coordenada do cliente pode estar errada).</div>
              )}
              <div style={styles.detailBlock}>
                <span style={styles.detailK}>Resumo / observação</span>
                <p style={{ margin: '4px 0 0', fontSize: 14, color: '#333' }}>{ci.notes || '—'}</p>
              </div>
              <div style={styles.detailBlock}>
                <span style={styles.detailK}>Localização do check-in</span>
                <div><a href={`https://www.google.com/maps?q=${ci.location.latitude},${ci.location.longitude}`} target="_blank" rel="noreferrer" style={{ color: '#f5821f', fontSize: 13 }}>{ci.location.latitude.toFixed(5)}, {ci.location.longitude.toFixed(5)}</a></div>
              </div>
              {((ci.photos?.length || 0) + (ci.checkoutPhotos?.length || 0)) > 0 && (
                <div style={styles.detailBlock}>
                  <span style={styles.detailK}>Fotos</span>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {(ci.photos || []).map((url, i) => (<a key={'in' + i} href={url} target="_blank" rel="noreferrer" title="Check-in"><FotoThumb uri={url} style={styles.photoThumb} /></a>))}
                    {(ci.checkoutPhotos || []).map((url, i) => (<a key={'out' + i} href={url} target="_blank" rel="noreferrer" title="Check-out"><FotoThumb uri={url} style={{ ...styles.photoThumb, borderColor: '#dc2626' }} /></a>))}
                  </div>
                </div>
              )}
              <div style={styles.modalFooter}>
                {temTrajeto && <button style={styles.trajetoBtn} onClick={() => { setDetail(null); setTrajeto(ci); }}>🗺️ Ver trajeto</button>}
                <button style={styles.cancelBtn} onClick={() => setDetail(null)}>Fechar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {editing && (
        <div style={styles.overlay} onClick={() => setEditing(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Editar check-in</h2>
            <p style={styles.modalSub}>{editing.clientName} • {editing.sellerName}</p>
            <label style={styles.label}>Observação</label>
            <textarea style={styles.textarea} value={editNotes} onChange={e => setEditNotes(e.target.value)} />
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setEditing(null)}>Cancelar</button>
              <button style={styles.saveBtn} onClick={saveEdit}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  exportBtn: { background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  // `flexWrap` é exigência da faixa de aviso (`avisoDup`, com flex-basis 100%): sem ele a faixa
  // divide a linha com a busca e some de vista em telas estreitas.
  filters: { display: 'flex', flexWrap: 'wrap' as const, gap: 12, marginBottom: 20 },
  search: { flex: 1, border: '1px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', fontSize: 15, background: '#fff' },
  select: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14, background: '#fff', cursor: 'pointer' },
  tableWrapper: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 800 },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0', background: '#fafafa', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  // Possível duplicada: tom de AVISO, nunca de erro. O servidor não afirma que há duplicidade nem
  // alterou coisa alguma — ele só notou que o mesmo vendedor tem duas visitas abertas no mesmo
  // cliente a menos de 12 h, e quem decide é quem esteve lá.
  avisoDup: { flex: '1 1 100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fef3c7', borderRadius: 8, padding: '9px 12px' },
  avisoDupTexto: { fontSize: 13, color: '#92400e' },
  avisoDupLink: { background: 'none', border: 'none', padding: 0, fontSize: 13, color: '#92400e', textDecoration: 'underline', cursor: 'pointer', whiteSpace: 'nowrap' as const },
  seloDup: { background: '#fde68a', color: '#78350f', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' as const },
  td: { padding: '12px 16px', fontSize: 14, color: '#333' },
  photoThumb: { width: 36, height: 36, borderRadius: 6, objectFit: 'cover', border: '1px solid #e5e7eb', cursor: 'pointer' },
  actionBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6, color: '#555' },
  deleteBtn: { background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#dc2626' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 16, width: '90%', maxWidth: 440, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalTitle: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  modalSub: { fontSize: 13, color: '#666', margin: '4px 0 16px' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 4 },
  textarea: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', minHeight: 90, fontFamily: 'inherit', resize: 'vertical' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  cancelBtn: { background: '#f3f4f6', color: '#444', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  trajetoBtn: { display: 'block', marginTop: 6, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 6, padding: '4px 8px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginTop: 8 },
  detailItem: { display: 'flex', flexDirection: 'column', fontSize: 14, color: '#333' },
  detailK: { fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 },
  detailBlock: { marginTop: 12 },
  detailWarn: { marginTop: 12, background: '#fef3c7', color: '#b45309', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontWeight: 600 },
  trajetoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f0f0f0', gap: 10 },
  trajetoLegend: { display: 'flex', gap: 18, flexWrap: 'wrap', padding: '12px 20px', fontSize: 13, color: '#444' },
  swatch: { display: 'inline-block', width: 14, height: 4, marginRight: 6, verticalAlign: 'middle' },
};
